import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FavoriteButton from "@/components/base/FavoriteButton";
import ShareBar from "@/components/base/ShareBar";
import NewsCard from "@/components/feature/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { getNewsById, newsList } from "@/mocks/news";

export default function NewsDetailPage() {
  const { id } = useParams();
  const item = getNewsById(id);

  useSeo(
    item ? `${item.title} - age动漫资讯` : "资讯内容未找到 - age动漫",
    item ? item.summary : "该资讯内容可能已被移除或链接有误，返回资讯列表查看最新动漫动态。",
    item ? `${item.tag},age动漫,动漫资讯,${item.title}` : "age动漫,资讯未找到",
  );

  if (!item) {
    return (
      <PageShell>
        <div className="w-full px-4 md:px-6 lg:px-10 py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "资讯", to: "/news" }, { label: "内容未找到" }]} />
          <div className="mt-6 max-w-2xl mx-auto">
            <EmptyState
              title="资讯内容未找到"
              description="这条资讯可能已被移除，或者链接地址有误。可以返回资讯列表，查看最新的动漫动态。"
              icon="ri-file-warning-line"
              action={
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回资讯列表
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    回到首页
                  </Link>
                </div>
              }
            />
          </div>
        </div>
      </PageShell>
    );
  }

  const related = newsList
    .filter((news) => news.id !== item.id && news.tag === item.tag)
    .concat(newsList.filter((news) => news.id !== item.id && news.tag !== item.tag))
    .slice(0, 3);

  return (
    <PageShell>
      <article>
        <header className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-9">
            <Breadcrumb
              items={[{ label: "首页", to: "/" }, { label: "资讯", to: "/news" }, { label: item.tag, to: "/news" }]}
            />
            <div className="mt-4 max-w-4xl">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Link
                  to="/news"
                  className="px-2 py-0.5 rounded bg-primary-50 text-primary-700 text-[11px] hover:bg-primary-100 transition-colors cursor-pointer"
                >
                  {item.tag}
                </Link>
                <span className="text-[11px] text-foreground-400">{item.publishedAt}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">{item.title}</h1>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{item.summary}</p>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] md:text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {item.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {item.views.toLocaleString("zh-CN")} 次阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-bookmark-line"></i>
                  资讯 / {item.tag}
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8 lg:gap-10">
            <div className="min-w-0">
              <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title} - age动漫资讯正文配图`}
                  title={`${item.title} - 动漫资讯`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <p className="mt-2 text-[11px] text-foreground-400 text-center">配图 · age动漫内容编辑部</p>

              <div className="mt-6 md:mt-8 space-y-4 md:space-y-5">
                {item.content.map((paragraph, index) => (
                  <p
                    key={index}
                    className={`text-[15px] leading-7 md:leading-8 text-foreground-700 ${
                      index === 0 ? "text-foreground-800" : ""
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <p className="text-xs text-foreground-500 leading-relaxed">
                  本文为演示内容，用于展示资讯阅读体验，不代表任何真实报道。
                </p>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回资讯列表
                </Link>
              </div>
            </div>

            <aside className="lg:sticky lg:top-24 h-fit flex flex-col gap-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="text-sm font-semibold text-foreground-900 mb-3">收藏与分享</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <FavoriteButton type="news" id={item.id} title={item.title} />
                </div>
                <div className="mt-3 pt-3 border-t border-background-100">
                  <ShareBar />
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="text-sm font-semibold text-foreground-900 mb-3">所属分类</h2>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/news"
                    className="px-2.5 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {item.tag}
                  </Link>
                  <Link
                    to="/news"
                    className="px-2.5 py-1.5 rounded-md bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    全部资讯
                  </Link>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="text-sm font-semibold text-foreground-900 mb-3">相关资讯</h2>
                <ul className="flex flex-col gap-3">
                  {related.map((news) => (
                    <li key={news.id}>
                      <Link
                        to={`/news/detail/${news.id}`}
                        className="group block cursor-pointer"
                      >
                        <p className="text-xs font-medium text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {news.title}
                        </p>
                        <span className="text-[10px] text-foreground-400 mt-1 inline-block">{news.publishedAt}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>

          <section className="mt-10 md:mt-14">
            <SectionHeading kicker="MORE" title="继续阅读" actionLabel="全部资讯" actionTo="/news" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((news) => (
                <NewsCard key={news.id} item={news} />
              ))}
            </div>
          </section>
        </div>
      </article>
    </PageShell>
  );
}